// client/src/views/Sidebar.jsx
import React, { useState, useEffect } from "react";
import { SidebarHeader, PoiLabelControls } from "./Sidebar_parts/SidebarHeaderAndLabels";
import RegionMapsDrawer from "./Sidebar_parts/RegionMapsDrawer";
import POISection from "./Sidebar_parts/POISection";
import PathsAndZonesSection from "./Sidebar_parts/PathsAndZonesSection";
import SidebarBottomBanner from "./Sidebar_parts/SidebarBottomBanner";

const TIP_BAR_HEIGHT = 68;
const TIP_BAR_HEIGHT_MOBILE = 56;

const ADS_BANNER_W = 320;
const ADS_BANNER_H = 50;

const MIN_LABEL_SCALE = 0.2;
const MAX_LABEL_SCALE = 1;
const DEFAULT_LABEL_SCALE = 0.4;

function readDisplayMetrics() {
  try {
    if (typeof window === "undefined") return { physShort: 0, physLong: 0, vw: 0, vh: 0 };
    const dpr = Number(window.devicePixelRatio) || 1;
    const sw = window.screen ? Number(window.screen.width) : 0;
    const sh = window.screen ? Number(window.screen.height) : 0;

    const physW = Math.round((Number.isFinite(sw) ? sw : 0) * (Number.isFinite(dpr) && dpr > 0 ? dpr : 1));
    const physH = Math.round((Number.isFinite(sh) ? sh : 0) * (Number.isFinite(dpr) && dpr > 0 ? dpr : 1));

    const physLong = Math.max(physW, physH);
    const physShort = Math.min(physW, physH);

    const vw = Number.isFinite(Number(window.innerWidth)) ? Number(window.innerWidth) : 0;
    const vh = Number.isFinite(Number(window.innerHeight)) ? Number(window.innerHeight) : 0;

    return { physShort, physLong, vw, vh };
  } catch {
    return { physShort: 0, physLong: 0, vw: 0, vh: 0 };
  }
}

function is1440pOrHigher() {
  const { physShort, physLong, vw, vh } = readDisplayMetrics();
  const looksLike1440p = physShort >= 1440 && physLong >= 2560;
  const viewportNotTiny = vw >= 1100 && vh >= 650;
  return looksLike1440p && viewportNotTiny;
}

function Sidebar({
  sidebarWidth,
  sidebarRef,
  logo,
  mapDisplayName,
  gameName,
  voteStatus,
  handleVote,
  user,
  isFavourited,
  favLoading,
  handleFavourite,
  customCategories,
  customTypes,
  typeColours,
  visibleTypes,
  visibleCategories,
  expandedCategories,
  setExpandedCategories,
  expandedTypes,
  setExpandedTypes,
  toggleAllTypes,
  allTypesVisible,
  toggleCategoryExpand,
  toggleCategory,
  toggleType,
  handleMouseDown,
  pois,
  zones = [],
  visibleZones,
  setVisibleZones,
  hoveredZoneId,
  setHoveredZoneId,
  tipUrl,
  poiLabelToggleBtn,
  isMobile = false,
  onMobileTypeClick,
  onClose,
  onUserLogin,
  hoveredType,
  hoveredPoiId,
  setHoveredPoiId,
  handleTypeHover,
  handleTypeUnhover,
  setHideSidebarHover,
  isMapOwner,
  extraRegionMaps = [],
  onAddNewRegionMap,
  onRegionMapClick,
  parentMapId,
  styles = {},
  mapId,
  pathControls = {},
  onPathControlChange,
  onToggleZoneUserVisibility,
  activeOverlayId = null,
  hasActiveOverlay = false,
  isPublic = 0,
  seoApproved = 0,
  allowAds = 0,
  isEmbed = false,
  rememberMapPrefs = true,
}) {
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [anonVotes, setAnonVotes] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [labelScale, setLabelScale] = useState(() => {
    if (!rememberMapPrefs) return DEFAULT_LABEL_SCALE;
    if (typeof window !== "undefined") {
      try {
        const key = mapId ? `poiLabelScale_${mapId}` : "poiLabelScale";
        const stored = window.localStorage.getItem(key);
        if (stored != null) {
          const v = parseFloat(stored);
          if (!Number.isNaN(v) && v >= MIN_LABEL_SCALE && v <= MAX_LABEL_SCALE) {
            return v;
          }
        }
      } catch {}
    }
    return DEFAULT_LABEL_SCALE;
  });

  const [largeAdsMode, setLargeAdsMode] = useState(() => {
    if (typeof window === "undefined") return false;
    return is1440pOrHigher();
  });

  useEffect(() => {
    if (typeof window === "undefined") return;

    let raf = null;
    const onResize = () => {
      if (raf) return;
      raf = window.requestAnimationFrame(() => {
        raf = null;
        setLargeAdsMode(is1440pOrHigher());
      });
    };

    window.addEventListener("resize", onResize, { passive: true });
    window.addEventListener("orientationchange", onResize, { passive: true });

    return () => {
      if (raf) window.cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
    };
  }, []);

  const sidebarBg = (styles && styles.sidebar_bg_colour) || "#202124";
  const mainFont = (styles && styles.font_family) || "inherit";
  const fontColour = (styles && styles.font_colour) || "#fff";

  const sectionNamePOIs = (styles && styles.section_name_pois) || "POIs";
  const sectionNameZones = (styles && styles.section_name_zones) || "Zones";
  const sectionNameRegionMaps = (styles && styles.section_name_region_maps) || "Region Maps";
  const sectionNamePaths = (styles && styles.section_name_paths) || "Paths";

  const creatorSignature = styles?.creator_signature || null;
  const watermarkUrl = styles?.watermark_url || null;

  const safeVoteStatus = {
    total_votes: Number(
      voteStatus && typeof voteStatus.total_votes !== "undefined" ? voteStatus.total_votes : anonVotes ?? 0
    ),
    user_vote: Number(voteStatus?.user_vote ?? 0),
  };

  const hasTitleImage = Boolean(styles?.title_image_url);

  const normalizedSearch = searchQuery.trim().toLowerCase();
  const hasSearch = normalizedSearch.length > 0;

  const effectiveHasActiveOverlay = activeOverlayId != null ? true : !!hasActiveOverlay;

  const filteredPois = Array.isArray(pois)
    ? pois.filter((poi) => {
        let overlayId = poi.overlay_id != null ? poi.overlay_id : poi.overlayId != null ? poi.overlayId : null;

        if (overlayId === "" || overlayId === undefined) {
          overlayId = null;
        }

        if (overlayId === null) return true;

        const numericOverlayId = Number(overlayId);
        if (!Number.isFinite(numericOverlayId)) {
          return true;
        }

        if (numericOverlayId === 1) {
          return !effectiveHasActiveOverlay;
        }

        if (!effectiveHasActiveOverlay) return false;

        const activeIdNum = Number(activeOverlayId);
        if (!Number.isFinite(activeIdNum)) return false;

        return String(activeIdNum) === String(numericOverlayId);
      })
    : [];

  const hasPois = filteredPois.length > 0;

  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.style.setProperty("--poi-label-scale", String(labelScale));
    }
    if (typeof window !== "undefined") {
      if (!rememberMapPrefs) return;
      try {
        const key = mapId ? `poiLabelScale_${mapId}` : "poiLabelScale";
        window.localStorage.setItem(key, String(labelScale));
      } catch {}
    }
  }, [labelScale, rememberMapPrefs, mapId]);

  useEffect(() => {
    let ignore = false;
    async function fetchAnonTotals() {
      if (!mapId) return;
      try {
        const res = await fetch(`/api/map-votes/${mapId}/0`);
        if (!res.ok) throw new Error("Failed to fetch votes");
        const data = await res.json();
        if (!ignore) {
          const total = Number(data?.total_votes ?? 0);
          setAnonVotes(Number.isFinite(total) ? total : 0);
        }
      } catch {
        if (!ignore) setAnonVotes(0);
      }
    }

    const missingTotals = !voteStatus || typeof voteStatus.total_votes === "undefined";
    if (missingTotals) {
      fetchAnonTotals();
    } else {
      setAnonVotes(null);
    }

    return () => {
      ignore = true;
    };
  }, [mapId, voteStatus]);

  const handleVoteClick = (vote) => {
    if (!user) {
      setShowLoginPrompt(true);
      return;
    }
    setShowLoginPrompt(false);
    if (typeof handleVote === "function") {
      handleVote(vote);
    }
  };

  const handleUserLogin = (newUser) => {
    setShowLoginPrompt(false);
    if (typeof onUserLogin === "function") {
      onUserLogin(newUser);
    }
  };

  const adsEligibilityGate = Number(isPublic) === 1 && Number(seoApproved) === 1 && Number(allowAds) === 1 && !isEmbed;

  const isEligibleForAds = adsEligibilityGate;

  const baseTipBar = isMobile ? TIP_BAR_HEIGHT_MOBILE : TIP_BAR_HEIGHT;

  const adsGap = 6;

  const desktopAdsInnerHeight = ADS_BANNER_H * 2 + adsGap;
  const desktopAdsBarHeight = desktopAdsInnerHeight + 18;

  const desktopLargeInnerHeight = 250;
  const desktopLargeBarHeight = desktopLargeInnerHeight + 18;

  const mobileAdsInnerHeight = ADS_BANNER_H;
  const mobileAdsBarHeight = mobileAdsInnerHeight + 14;

  const adsInnerHeight = isMobile ? mobileAdsInnerHeight : largeAdsMode ? desktopLargeInnerHeight : desktopAdsInnerHeight;

  const adsBarHeight = isMobile ? mobileAdsBarHeight : largeAdsMode ? desktopLargeBarHeight : desktopAdsBarHeight;

  const tipBarHeight = isEligibleForAds ? adsBarHeight : tipUrl ? baseTipBar : baseTipBar + (isMobile ? 22 : 32);

  const mobileBaseFontSize = isMobile ? 13 : 14;
  const mobileEdgePad = isMobile ? 8 : 10;

  useEffect(() => {
    if (!isMobile) return;
    if (typeof window === "undefined" || typeof document === "undefined") return;

    const root = document.documentElement;

    const setVh = () => {
      const h =
        window.visualViewport && Number.isFinite(window.visualViewport.height) ? window.visualViewport.height : window.innerHeight;
      const px = Math.max(1, Math.round(h * 0.01 * 1000) / 1000);
      root.style.setProperty("--pa-vh", `${px}px`);
    };

    setVh();

    window.addEventListener("resize", setVh, { passive: true });
    window.addEventListener("orientationchange", setVh, { passive: true });

    const vv = window.visualViewport;
    if (vv) {
      vv.addEventListener("resize", setVh, { passive: true });
      vv.addEventListener("scroll", setVh, { passive: true });
    }

    return () => {
      window.removeEventListener("resize", setVh);
      window.removeEventListener("orientationchange", setVh);
      if (vv) {
        vv.removeEventListener("resize", setVh);
        vv.removeEventListener("scroll", setVh);
      }
    };
  }, [isMobile]);

  const sidebarHeight = isMobile ? "calc(var(--pa-vh, 1vh) * 100)" : "100vh";

  const topAdId = "Sidebar_banner_top";
  const bottomAdId = "Sidebar_banner_bottom";
  const largeAdId = "Sidebar_banner_large";

  const baseAdTargeting = {
    placement: "sidebar",
    map_id: mapId != null && mapId !== "" ? String(mapId) : "0",
    game: gameName ? String(gameName) : "",
  };

  return (
    <div
      className={`sidebar${hasTitleImage ? " has-title-image" : ""}`}
      style={{
        width: isMobile ? "100vw" : `${sidebarWidth}px`,
        height: sidebarHeight,
        maxHeight: sidebarHeight,
        display: "flex",
        flexDirection: "column",
        position: isMobile ? "fixed" : "relative",
        top: 0,
        left: 0,
        background: sidebarBg,
        fontFamily: mainFont,
        color: fontColour,
        zIndex: 2000,
        fontSize: mobileBaseFontSize,
        lineHeight: isMobile ? 1.2 : "normal",
        ...(isMobile && {
          minWidth: 0,
          maxWidth: "100vw",
          padding: 0,
          borderRadius: 0,
          boxShadow: "none",
        }),
        overflow: "hidden",
      }}
      ref={sidebarRef}
    >
      <SidebarHeader
        logo={logo}
        styles={styles}
        mapDisplayName={mapDisplayName}
        gameName={gameName}
        creatorSignature={creatorSignature}
        showLoginPrompt={showLoginPrompt}
        onVoteClick={handleVoteClick}
        safeVoteStatus={safeVoteStatus}
        isFavourited={isFavourited}
        favLoading={favLoading}
        handleFavourite={handleFavourite}
        user={user}
        onUserLogin={handleUserLogin}
        hasTitleImage={hasTitleImage}
        fontColour={fontColour}
        mainFont={mainFont}
        isMobile={isMobile}
        onClose={onClose}
      />

      <PoiLabelControls
        poiLabelToggleBtn={poiLabelToggleBtn}
        hasTitleImage={hasTitleImage}
        labelScale={labelScale}
        setLabelScale={setLabelScale}
        minLabelScale={MIN_LABEL_SCALE}
        maxLabelScale={MAX_LABEL_SCALE}
        isMobile={isMobile}
      />

      <RegionMapsDrawer
        isMapOwner={isMapOwner}
        extraRegionMaps={extraRegionMaps}
        parentMapId={parentMapId}
        mapDisplayName={mapDisplayName}
        onRegionMapClick={onRegionMapClick}
        sectionNameRegionMaps={sectionNameRegionMaps}
        user={user}
        onRegionMapsChanged={onAddNewRegionMap}
        isMobile={isMobile}
      />

      <div
        style={{
          flex: 1,
          overflowY: "auto",
          minHeight: 0,
          background: "transparent",
          paddingRight: isMobile ? mobileEdgePad : 10,
          paddingLeft: isMobile ? mobileEdgePad : 0,
          paddingBottom: 8,
          boxSizing: "border-box",
          WebkitOverflowScrolling: "touch",
        }}
      >
        <div style={{ marginBottom: isMobile ? 6 : "10px" }}>
          <input
            type="text"
            placeholder="Search POIs, types and zones..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              width: "100%",
              padding: "6px 8px",
              borderRadius: isMobile ? 8 : 6,
              border: "1px solid #3b3c40",
              backgroundColor: "#18191c",
              color: "#fff",
              fontSize: isMobile ? 13 : 14,
              outline: "none",
              boxSizing: "border-box",
            }}
          />
        </div>

        {hasPois && (
          <POISection
            customCategories={customCategories}
            customTypes={customTypes}
            typeColours={typeColours}
            pois={filteredPois}
            visibleTypes={visibleTypes}
            visibleCategories={visibleCategories}
            expandedCategories={expandedCategories}
            setExpandedCategories={setExpandedCategories}
            expandedTypes={expandedTypes}
            setExpandedTypes={setExpandedTypes}
            toggleAllTypes={toggleAllTypes}
            allTypesVisible={allTypesVisible}
            toggleCategoryExpand={toggleCategoryExpand}
            toggleCategory={toggleCategory}
            toggleType={toggleType}
            isMobile={isMobile}
            onMobileTypeClick={onMobileTypeClick}
            handleTypeHover={handleTypeHover}
            handleTypeUnhover={handleTypeUnhover}
            setHideSidebarHover={setHideSidebarHover}
            setHoveredPoiId={setHoveredPoiId}
            fontColour={fontColour}
            mainFont={mainFont}
            sectionNamePOIs={sectionNamePOIs}
            hasSearch={hasSearch}
            normalizedSearch={normalizedSearch}
          />
        )}

        <PathsAndZonesSection
          zones={zones}
          visibleZones={visibleZones}
          setVisibleZones={setVisibleZones}
          pathControls={pathControls}
          onPathControlChange={onPathControlChange}
          mapId={mapId}
          sectionNamePaths={sectionNamePaths}
          sectionNameZones={sectionNameZones}
          isMapOwner={isMapOwner}
          onToggleZoneUserVisibility={onToggleZoneUserVisibility}
          setHoveredZoneId={setHoveredZoneId}
          setHideSidebarHover={setHideSidebarHover}
          hasSearch={hasSearch}
          normalizedSearch={normalizedSearch}
          activeOverlayId={activeOverlayId}
          hasActiveOverlay={effectiveHasActiveOverlay}
          rememberMapPrefs={rememberMapPrefs}
        />
      </div>

      <SidebarBottomBanner
        isEligibleForAds={isEligibleForAds}
        isMobile={isMobile}
        tipUrl={tipUrl}
        adsBarHeight={adsBarHeight}
        adsInnerHeight={adsInnerHeight}
        adsGap={adsGap}
        largeAdsMode={largeAdsMode}
        ADS_BANNER_W={ADS_BANNER_W}
        ADS_BANNER_H={ADS_BANNER_H}
        baseAdTargeting={baseAdTargeting}
        topAdId={topAdId}
        bottomAdId={bottomAdId}
        largeAdId={largeAdId}
        tipBarHeight={tipBarHeight}
      />

      {watermarkUrl && (
        <img
          src={watermarkUrl}
          alt="Map Watermark"
          style={{
            position: "absolute",
            bottom: 32 + tipBarHeight,
            left: "50%",
            transform: "translateX(-50%)",
            width: "85%",
            maxWidth: 330,
            opacity: 0.1,
            pointerEvents: "none",
            userSelect: "none",
            zIndex: 0,
          }}
        />
      )}

      {!isMobile && <div className="resizer" onMouseDown={handleMouseDown} />}
    </div>
  );
}

export default Sidebar;
